import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../../../Header';
import Footer from '../../../Footer';
import { incomingShipmentApi } from '../../../../api/incomingShipmentApi';
import { checkIsAdminLoggined } from '../../../../utils/utils';
import { getApiErrorText } from '../../../../utils/apiResponse';
import IncomingShipmentsDesktopView from './IncomingShipmentsDesktopView';
import IncomingShipmentsMobileView from './IncomingShipmentsMobileView';
import IncomingDeleteItemConfirmModal from './IncomingDeleteItemConfirmModal';

const IncomingShipmentsPage = props => {
  const [shipments, setShipments] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deletingShipmentId, setDeletingShipmentId] = useState(null);
  const deletingRef = useRef(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [savingShipmentStatusId, setSavingShipmentStatusId] = useState(null);

  const mounted = useRef(false);
  const loadSequence = useRef(0);
  const creatingRef = useRef(false);
  const savingStatusRef = useRef(false);
  const routeProps = useRef(props);

  const loadShipments = useCallback(async () => {
    const request = ++loadSequence.current;
    setLoading(true);
    try {
      const res = await incomingShipmentApi.getShipments();
      if (mounted.current && request === loadSequence.current) setShipments(res.data?.data || []);
    } catch (e) {
      if (mounted.current && request === loadSequence.current) setError(getApiErrorText(e));
    } finally {
      if (mounted.current && request === loadSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    checkIsAdminLoggined(routeProps.current);
    loadShipments();
    return () => {
      mounted.current = false;
      loadSequence.current += 1;
    };
  }, [loadShipments]);

  const patchShipmentStatus = async (id, status) => {
    if (
      id == null ||
      !status ||
      pendingDelete ||
      savingStatusRef.current ||
      deletingRef.current ||
      creatingRef.current
    )
      return;
    savingStatusRef.current = true;
    setSavingShipmentStatusId(id);
    setError('');
    try {
      const res = await incomingShipmentApi.patchShipment(id, status);
      if (!mounted.current) return;
      const next = res.data?.data?.status ?? status;
      setShipments(list => list.map(row => (row.id === id ? { ...row, status: next } : row)));
    } catch (e) {
      if (mounted.current) setError(getApiErrorText(e));
    } finally {
      savingStatusRef.current = false;
      if (mounted.current) setSavingShipmentStatusId(null);
    }
  };

  const requestDeleteShipment = shipment => {
    if (
      deletingRef.current ||
      savingStatusRef.current ||
      creatingRef.current ||
      !shipment.canDelete
    )
      return;
    setPendingDelete(shipment);
  };

  const cancelDeleteShipment = useCallback(() => {
    if (!deletingRef.current) setPendingDelete(null);
  }, []);

  const deleteShipment = async () => {
    const shipment = pendingDelete;
    if (!shipment || deletingRef.current || savingStatusRef.current || creatingRef.current) return;
    deletingRef.current = true;
    setDeletingShipmentId(shipment.id);
    setError('');
    try {
      await incomingShipmentApi.deleteShipment(shipment.id);
      if (!mounted.current) return;
      // An older list request must not restore the deleted shipment.
      loadSequence.current += 1;
      setLoading(false);
      setShipments(list => list.filter(row => row.id !== shipment.id));
      setPendingDelete(null);
    } catch (e) {
      if (!mounted.current) return;
      setError(getApiErrorText(e));
      setPendingDelete(null);
      await loadShipments();
    } finally {
      deletingRef.current = false;
      if (mounted.current) setDeletingShipmentId(null);
    }
  };

  const createShipment = async () => {
    const name = (newName || '').trim();
    if (
      !name ||
      pendingDelete ||
      creatingRef.current ||
      deletingRef.current ||
      savingStatusRef.current
    )
      return;
    creatingRef.current = true;
    setCreating(true);
    setError('');
    try {
      await incomingShipmentApi.createShipment(name);
      if (!mounted.current) return;
      setNewName('');
      await loadShipments();
    } catch (e) {
      if (mounted.current) setError(getApiErrorText(e));
    } finally {
      creatingRef.current = false;
      if (mounted.current) setCreating(false);
    }
  };

  const viewProps = {
    busy:
      Boolean(pendingDelete) ||
      creating ||
      deletingShipmentId != null ||
      savingShipmentStatusId != null,
    shipments,
    newName,
    setNewName,
    createShipment,
    creating,
    loading,
    error,
    savingShipmentStatusId,
    onPatchShipmentStatus: patchShipmentStatus,
    deletingShipmentId,
    onDeleteShipment: requestDeleteShipment,
  };

  return (
    <div className='container'>
      <div className='row'>
        <div className='col p-0'>
          <Header />
        </div>
      </div>

      <div className='d-none d-lg-block'>
        <IncomingShipmentsDesktopView {...viewProps} />
      </div>

      <div className='d-lg-none'>
        <IncomingShipmentsMobileView {...viewProps} />
      </div>

      <IncomingDeleteItemConfirmModal
        open={Boolean(pendingDelete)}
        title='Видалити поставку?'
        description={
          <>
            Поставку{' '}
            <strong className='text-dark text-break'>
              «{pendingDelete?.name}» (#{pendingDelete?.id})
            </strong>{' '}
            буде видалено разом з очікуваними товарами та завантаженими файлами.
          </>
        }
        onCancel={cancelDeleteShipment}
        onConfirm={deleteShipment}
        submitting={deletingShipmentId != null}
      />

      <div className='row'>
        <div className='col p-0'>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default IncomingShipmentsPage;
